import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Account } from "bonusx-api-main-manager";
import { useIsLogged } from "../../../hooks/auth/use-is-logged/use-is-logged";
import { useAbility } from "../../../legacy/hooks/useAbilityRules";
import * as accountApi from "./account.api";
import { accountQueryKeys } from "./account.query-keys";

export interface IUseAccountQuery {
  accountData: Account | undefined;
  revalidate(): Promise<void>;
  loading: boolean;
  error: Error | undefined;
  accountApi: typeof accountApi.accountApi;
}

export function useAccountQuery(): IUseAccountQuery {
  const queryClient = useQueryClient();
  const isLogged = useIsLogged();
  const abilityRules = useAbility();
  const canReadAccount = abilityRules.can("readOne", "Account");
  const enabled = isLogged && canReadAccount;

  const query = useQuery({
    queryKey: accountQueryKeys.me(),
    queryFn: accountApi.getMe,
    enabled,
    // cacheTime: 0 is deliberate: drop cache on unmount so revisits always refetch (removed by mistake in #1914)
    cacheTime: 0,
  });

  async function revalidate(): Promise<void> {
    await queryClient.invalidateQueries({
      queryKey: accountQueryKeys.me(),
    });
  }

  return {
    accountData: query.data,
    revalidate,
    loading: query.isInitialLoading,
    error: query.error as Error | undefined,
    accountApi: accountApi.accountApi,
  };
}
